import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pause, Play } from "lucide-react";
import { api } from "@/lib/api";
import { ukDay, ukTime } from "@/lib/time";
import { Panel, StatStrip, Tabs, Sw, Empty, CellSub } from "@/components/portal/crm/kit";
import { ConnieMark } from "@/components/connie/ConnieMark";
import { ConnieChat } from "@/components/connie/ConnieChat";
import { ConnieText } from "@/components/connie/ConnieText";
import { MeetConnie } from "@/components/hub/MeetConnie";
import { ConniePowers } from "@/components/connie/ConniePowers";
import { ReplyToPartner } from "@/components/hub/ReplyToPartner";

export const pounds = (v) => {
  const n = Number(v || 0);
  if (n === 0) return "£0";
  if (n < 0.01) return `£${n.toFixed(4)}`;
  if (n < 10) return `£${n.toFixed(2)}`;
  return `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
};

const plain = (t) => (t || "").replace(/\*\*/g, "").replace(/\[[^\]]*\]\(mailto:([^)\s]+)\)/g, "$1").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

const KIND = {
  auto_reply: "Answered a partner in Messages", chat_reply: "Replied in Messages", chase: "Chased the desks",
  report: "Passed something to the team", feedback: "New feedback", summary: "Summarised a case",
  case_reply: "Replied on a case thread", paused: "Paused", resumed: "Switched back on",
  outreach: "Messaged partners", weekly_tidy: "Monday tidy-up", partner_change: "Changed a partner's settings",
  self_change: "Changed settings a partner asked for", link_sent: "Sent a link or paperwork",
  desk_update: "Updated a case for a desk", doc_chase: "Chased a client for documents", owner_text: "Owner morning text", owner_reply: "Did a job you texted her",
  client_reply: "Answered a client by text", client_handoff: "Passed a client to their adviser",
};

/** The Connie control room — switch her on and off, see what she costs, shape how she talks. */
export default function HubConnie() {
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setOv((await api.get("/hub/connie/overview")).data); } catch { toast.error("Couldn't load Connie's numbers"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.put("/hub/connie/settings", { enabled: !ov.enabled });
      toast.success(ov.enabled ? "Connie is paused everywhere" : "Connie is back on");
      load();
    } catch { toast.error("That didn't save"); }
    finally { setBusy(false); }
  };

  if (!ov) return <Empty testId="hub-connie-loading">Opening the control room…</Empty>;
  const live = Object.values(ov.surfaces || {}).filter(Boolean).length;

  return (
    <div data-testid="hub-connie">
      <Hero ov={ov} live={live} busy={busy} onToggle={toggle} />
      {ov.over_alert && (
        <div className="warn" style={{ marginTop: 18 }} data-testid="hub-connie-over-alert">
          <span><b>Heads up:</b> this month&apos;s spend ({pounds(ov.spend.this_month.cost)}) has passed your alert line of {pounds(ov.monthly_alert_gbp)}. Nothing has been switched off — it&apos;s your call.</span>
        </div>
      )}
      <div style={{ marginTop: 22 }}>
        <StatStrip testId="hub-connie-spend" items={[
          { k: "Spend today", v: pounds(ov.spend.today.cost), h: `${ov.spend.today.calls} calls`, testId: "hub-connie-spend-today" },
          { k: "Last 7 days", v: pounds(ov.spend.week.cost), h: `${ov.spend.week.calls} calls`, testId: "hub-connie-spend-week" },
          { k: "This month", v: pounds(ov.spend.this_month.cost), h: `${pounds(ov.spend.all_time.cost)} all time`, testId: "hub-connie-spend-month" },
          { k: "Avg per chat", v: pounds(ov.avg_cost_per_chat), h: `${pounds(ov.avg_cost_per_call)} per call`, testId: "hub-connie-avg-chat" },
        ]} />
      </div>
      <div style={{ marginTop: 12 }}>
        <StatStrip testId="hub-connie-work" items={[
          { k: "Conversations", v: ov.conversations, h: "with partners and the team", testId: "hub-connie-conversations" },
          { k: "Auto-replies", v: ov.auto_replies, h: `${ov.chat_replies} more on request`, testId: "hub-connie-auto-replies" },
          { k: "Chases made", v: ov.chases, h: "anonymous nudges to desks", testId: "hub-connie-chases" },
          { k: "Passed to you", v: ov.reports, h: `${ov.open_feedback} feedback items open`, testId: "hub-connie-reports" },
        ]} />
      </div>
      <div style={{ marginTop: 30 }}>
        <Tabs value={tab} onChange={setTab} testPrefix="hub-connie-tab" items={[
          { key: "overview", label: "Overview" }, { key: "ask", label: "Ask Connie" },
          { key: "conversations", label: "Conversations", count: ov.conversations },
          { key: "powers", label: "What she can do" },
          { key: "personality", label: "Personality & knowledge" }, { key: "surfaces", label: "Where she works" },
          { key: "announce", label: "Introduce her" },
        ]} />
      </div>
      <div style={{ marginTop: 20 }} className="connie-in" key={tab}>
        {tab === "overview" && <Overview ov={ov} />}
        {tab === "ask" && <ConnieChat testPrefix="hub-connie-ask" height="calc(100dvh - 220px)" />}
        {tab === "conversations" && <Conversations />}
        {tab === "powers" && <ConniePowers />}
        {tab === "personality" && <Personality onSaved={load} />}
        {tab === "surfaces" && <Surfaces onSaved={load} />}
        {tab === "announce" && <MeetConnie />}
      </div>
    </div>
  );
}

function Hero({ ov, live, busy, onToggle }) {
  return (
    <div className="connie-hero" data-testid="hub-connie-hero">
      <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 34, flexWrap: "wrap" }}>
        <ConnieMark size={132} state={ov.enabled ? "idle" : "idle"} testId="hub-connie-hero-mark" />
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ font: '800 11px/1 Archivo', letterSpacing: ".2em", textTransform: "uppercase", color: "#7DA9FF" }}>
            Connie · {ov.model}
          </div>
          <h1 style={{ font: '800 clamp(30px,4vw,46px)/1.02 Archivo', letterSpacing: "-.035em", margin: "12px 0 0", color: "#fff" }}>
            Connie control room
          </h1>
          <p style={{ color: "rgba(230,238,255,.72)", fontSize: 14.5, lineHeight: 1.6, margin: "12px 0 0", maxWidth: 560 }}>
            She answers lenders, introducers, call centres and media partners, chases desks, and passes everything that needs
            you straight here. Anonymity is enforced in code — she is never given a name she can&apos;t share.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
          <span className={`st ${ov.enabled ? "st-off" : "st-sent"}`} data-testid="hub-connie-status">
            <i className="sq" />{ov.enabled ? `Live · ${live} places` : "Paused everywhere"}
          </span>
          <button className={`btn ${ov.enabled ? "btn-ghost" : "btn-primary"}`} onClick={onToggle} disabled={busy}
                  data-testid="hub-connie-toggle">
            {busy ? <Loader2 className="animate-spin" /> : ov.enabled ? <Pause /> : <Play />}
            {ov.enabled ? "Pause Connie" : "Switch Connie on"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Overview({ ov }) {
  const max = Math.max(...ov.daily.map((d) => d.cost), 0.0001);
  const maxS = Math.max(...ov.by_surface.map((s) => s.cost), 0.0001);
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] items-start">
      <div className="space-y-5">
        <ReplyStats r={ov.replies} />
        <Panel title="Spend, last 30 days" sub={`${pounds(ov.spend.days30.cost)} across ${ov.spend.days30.calls} calls · ${(ov.spend.days30.tokens_in + ov.spend.days30.tokens_out).toLocaleString()} tokens`} testId="hub-connie-daily">
          <div className="connie-spark">
            {ov.daily.map((d) => (
              <span key={d.day} className={d.cost ? "" : "zero"} title={`${ukDay(d.day)} · ${pounds(d.cost)} · ${d.calls} calls`}
                    style={{ height: `${Math.max(4, (d.cost / max) * 100)}%` }} />
            ))}
          </div>
          <CellSub style={{ marginTop: 12 }}>Estimated from tokens at your prices (Where she works). Your exact balance is on the Universal Key in your Emergent profile.</CellSub>
        </Panel>
        <Panel title="Where the spend goes" sub="Last 30 days" testId="hub-connie-surfaces-spend">
          {ov.by_surface.length === 0 ? <CellSub>Nothing spent yet.</CellSub> : ov.by_surface.map((s) => (
            <div key={s.surface} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                <b>{s.label}</b><span style={{ color: "var(--mut)" }}>{pounds(s.cost)} · {s.calls} calls</span>
              </div>
              <div className="connie-bar"><span style={{ width: `${(s.cost / maxS) * 100}%` }} /></div>
            </div>
          ))}
        </Panel>
      </div>
      <Activity />
    </div>
  );
}

const ROWS = [
  { k: "replies", label: "Replies in" }, { k: "by_email", label: "By email" }, { k: "by_text", label: "By text" },
  { k: "partners", label: "Partners who replied" }, { k: "handled_alone", label: "Connie handled alone" },
  { k: "needed_person", label: "Needed a person" },
];

function Delta({ now, before }) {
  if (before == null || now === before) return <span style={{ color: "var(--mut)" }}>same</span>;
  const up = now > before;
  return <span style={{ color: up ? "var(--accent-text)" : "var(--mut)" }}>{up ? "▲" : "▼"} {Math.abs(now - before)}</span>;
}

function ReplyStats({ r }) {
  if (!r) return null;
  const tw = r.this_week, lw = r.last_week;
  const maxR = Math.max(...tw.reasons.map((x) => x.count), 1);
  return (
    <Panel title="Replies by email & text" testId="hub-connie-replies"
           sub={`This week (since ${ukDay(r.week_starts)}) against last week · partners replying to our emails and texts`}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 90px 90px 70px", rowGap: 10, fontSize: 13, alignItems: "baseline" }}>
        <span className="rail-t">What</span><span className="rail-t" style={{ textAlign: "right" }}>This week</span>
        <span className="rail-t" style={{ textAlign: "right" }}>Last week</span><span />
        {ROWS.map(({ k, label }) => (
          <React.Fragment key={k}>
            <span>{label}</span>
            <b style={{ textAlign: "right", fontSize: 15 }} data-testid={`hub-connie-replies-${k}`}>{tw[k]}</b>
            <span style={{ textAlign: "right", color: "var(--mut)" }} data-testid={`hub-connie-replies-last-${k}`}>{lw[k]}</span>
            <span style={{ textAlign: "right", fontSize: 11.5 }}><Delta now={tw[k]} before={lw[k]} /></span>
          </React.Fragment>
        ))}
      </div>
      <CellSub style={{ marginTop: 14 }} data-testid="hub-connie-replies-alone-pct">
        {tw.handled_alone_pct == null ? "No partner replies yet this week."
          : `Connie answered ${tw.handled_alone_pct}% on her own this week${lw.handled_alone_pct != null ? ` (${lw.handled_alone_pct}% last week)` : ""}.`}
        {tw.ignored_auto ? ` ${tw.ignored_auto} automatic out-of-office repl${tw.ignored_auto === 1 ? "y" : "ies"} ignored.` : ""}
      </CellSub>
      <div className="rail-t" style={{ margin: "18px 0 10px" }}>Why she stayed quiet</div>
      {tw.reasons.length === 0 ? <CellSub data-testid="hub-connie-replies-reasons-empty">She hasn&apos;t held back on anything this week.</CellSub> : tw.reasons.map((x) => (
        <div key={x.code} style={{ marginBottom: 11 }} data-testid={`hub-connie-replies-reason-${x.code}`}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 5 }}>
            <span>{x.label}</span><b>{x.count}</b>
          </div>
          <div className="connie-bar"><span style={{ width: `${(x.count / maxR) * 100}%` }} /></div>
        </div>
      ))}
    </Panel>
  );
}

function Activity() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/hub/connie/activity?limit=40").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <Panel title="What Connie did" sub="Every action she takes, newest first" pad={false} testId="hub-connie-activity">
      {items === null ? <Empty>Loading…</Empty> : items.length === 0 ? <Empty>Nothing yet — she&apos;s ready when you are.</Empty> : (
        <div style={{ maxHeight: 620, overflowY: "auto" }}>
          {items.map((a) => <ActivityRow key={a.id} a={a} onReplied={load} />)}
        </div>
      )}
    </Panel>
  );
}

const REPLIABLE = (a) => (a.handed || a.kind === "report" || a.kind === "feedback") && (a.thread_id || ["referrer", "lender"].includes(a.role));

function ActivityRow({ a, onReplied }) {
  const [open, setOpen] = useState(false);
  const replied = !!a.replied_at;
  return (
    <div style={{ padding: "13px 22px", borderBottom: "1px solid var(--line-soft)" }} data-testid={`hub-connie-activity-${a.id}`}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
        <b style={{ fontSize: 13 }}>{KIND[a.kind] || a.kind}</b>
        {replied ? <span className="st st-fund" style={{ height: 22 }} data-testid={`hub-connie-activity-replied-${a.id}`}><i className="sq" />You replied</span>
          : (a.handed || a.kind === "report") && <span className="st st-sent" style={{ height: 22 }}><i className="sq" />Needs you</span>}
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--mut)", whiteSpace: "nowrap" }}>{ukDay(a.at)} · {ukTime(a.at)}</span>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--mut)", marginTop: 3 }}>
        {a.who}{a.partner && a.partner !== a.who ? ` · ${a.partner}` : ""}{a.count ? ` · ${a.count} partner${a.count === 1 ? "" : "s"}` : ""}{a.reference ? ` · ${a.reference}` : ""}{a.case ? ` · ${a.case}` : ""}{a.desks ? ` · ${a.desks} desk${a.desks === 1 ? "" : "s"}` : ""}
      </div>
      {(a.text || a.summary || a.reason) && (
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--ink-3)", marginTop: 6 }} className={open ? undefined : "line-clamp-3"}>
          {a.reason ? `Why: ${a.reason}. ` : ""}{plain(a.text || a.summary)}
        </div>
      )}
      {replied && <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--ink-3)" }}><b>You replied:</b> {a.reply_note}</div>}
      {REPLIABLE(a) && !open && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setOpen(true)} data-testid={`hub-connie-activity-reply-open-${a.id}`}>
          {replied ? "Reply again" : "Reply"}
        </button>
      )}
      {open && <ReplyToPartner activityId={a.id} testId={`hub-connie-activity-reply-${a.id}`} onSent={() => { setOpen(false); onReplied(); }} />}
    </div>
  );
}

function Conversations() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/hub/connie/conversations", { params: { q } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const pick = async (id) => setOpen((await api.get(`/hub/connie/conversations/${id}`)).data);
  const ROLE = { admin: "Connexis team", lender: "Lender", referrer: "Introducer" };
  return (
    <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)] items-start">
      <Panel pad={false} testId="hub-connie-conv-list">
        <div style={{ padding: 14, borderBottom: "1px solid var(--line)" }}>
          <input className="inp" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by partner or topic" data-testid="hub-connie-conv-search" style={{ width: "100%" }} />
        </div>
        <div style={{ maxHeight: 640, overflowY: "auto" }}>
          {items === null ? <Empty>Loading…</Empty> : items.length === 0 ? <Empty>No conversations yet.</Empty> : items.map((s) => (
            <button key={s.id} onClick={() => pick(s.id)} data-testid={`hub-connie-conv-${s.id}`}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "13px 18px", border: 0, cursor: "pointer",
                      borderBottom: "1px solid var(--line-soft)", background: open?.session?.id === s.id ? "var(--accent-soft)" : "#fff" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <b style={{ fontSize: 13.5 }} className="truncate">{s.party_name || s.contact_name}</b>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--mut)", whiteSpace: "nowrap" }}>{ukDay(s.updated_at)}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--mut)", marginTop: 3 }}>{ROLE[s.role] || s.role} · {s.message_count} messages · {pounds(s.cost_gbp)}</div>
              <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 4 }} className="truncate">{s.title}</div>
            </button>
          ))}
        </div>
      </Panel>
      <Panel title={open ? (open.session.party_name || open.session.contact_name) : "Pick a conversation"}
             sub={open ? `${open.session.contact_name || ""} · ${open.messages.length} messages · ${pounds(open.session.cost_gbp)}` : "Read exactly what Connie said, word for word."}
             testId="hub-connie-transcript">
        {!open ? <CellSub>Every chat with Connie is saved per person.</CellSub> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: 640, overflowY: "auto" }}>
            {open.messages.map((m) => m.role === "user"
              ? <div key={m.id} style={{ alignSelf: "flex-end", maxWidth: "80%" }} className="connie-me">{m.text}</div>
              : <div key={m.id} style={{ display: "flex", gap: 10 }}><ConnieMark size={26} orbit={false} /><div className="connie-bubble" style={{ flex: 1 }}><ConnieText text={m.text} /></div></div>)}
          </div>
        )}
      </Panel>
    </div>
  );
}

function useSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/hub/connie/settings").then((r) => setS(r.data)).catch(() => toast.error("Couldn't load settings")); }, []);
  const save = async (patch, onSaved) => {
    try { setS((await api.put("/hub/connie/settings", patch)).data); toast.success("Saved — Connie uses it from her next message"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't save"); }
  };
  return [s, setS, save];
}

function Personality({ onSaved }) {
  const [s, setS, save] = useSettings();
  if (!s) return <Empty>Loading…</Empty>;
  const field = (key, label, sub, rows, ph) => (
    <Panel title={label} sub={sub} testId={`hub-connie-${key}-panel`}>
      <textarea value={s[key] || ""} onChange={(e) => setS({ ...s, [key]: e.target.value })} rows={rows} placeholder={ph}
                className="w-full rounded-[12px] border px-4 py-3 text-[13.5px] leading-relaxed focus:outline-none"
                style={{ borderColor: "var(--line-2)", resize: "vertical" }} data-testid={`hub-connie-${key}`} />
    </Panel>
  );
  return (
    <div className="space-y-5">
      {field("personality", "Her personality", "How Connie sounds. She always writes in the Connexis voice and signs written messages 'The Connexis desk'.", 4)}
      {field("house_knowledge", "House knowledge", "Anything she should know that isn't on the site yet — offers, new lenders' appetite in general terms, process changes, FAQs. She trusts this over everything else.", 10,
        "e.g. This month we're prioritising bridging cases under £2m — turnaround is 48 hours.")}
      {field("rules", "Extra rules", "Things she must always or never do, on top of the built-in anonymity and safety rules.", 4,
        "e.g. Never quote a rate. Always suggest booking a call for cases over £5m.")}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-primary" onClick={() => save({ personality: s.personality, house_knowledge: s.house_knowledge, rules: s.rules }, onSaved)}
                data-testid="hub-connie-save-personality">Save how Connie talks</button>
      </div>
    </div>
  );
}

function Surfaces({ onSaved }) {
  const [s, setS, save] = useSettings();
  if (!s) return <Empty>Loading…</Empty>;
  const num = (key, label, step = "0.01") => (
    <label style={{ display: "block" }}>
      <div className="rail-t" style={{ marginBottom: 7 }}>{label}</div>
      <input className="inp" type="number" step={step} value={s[key] ?? ""} onChange={(e) => setS({ ...s, [key]: e.target.value === "" ? null : Number(e.target.value) })}
             data-testid={`hub-connie-${key}`} style={{ width: "100%" }} />
    </label>
  );
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] items-start">
      <Panel title="Where Connie can talk" sub="Switch any of these off and she stops there straight away. Everything else keeps working as normal." pad={false} testId="hub-connie-surfaces">
        {Object.entries(s.surface_labels).map(([key, label]) => {
          const [title, sub] = label.split(" — ");
          return (
            <div key={key} className="row" style={{ padding: "15px 22px", borderBottom: "1px solid var(--line-soft)", display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 13.5 }}>{title}</div>{sub && <div style={{ fontSize: 12.5, color: "var(--mut)", marginTop: 2 }}>{sub}</div>}
                <ChannelNote k={key} ch={s.channels} /></div>
              <Sw on={s.surfaces[key]} testId={`hub-connie-surface-${key}`}
                  onClick={() => save({ surfaces: { ...s.surfaces, [key]: !s.surfaces[key] } }, onSaved)} />
            </div>
          );
        })}
      </Panel>
      <Panel title="Costs & limits" sub="Used to estimate spend. Nothing is capped — you stay in control from the switch at the top." testId="hub-connie-pricing">
        <div className="grid grid-cols-2 gap-4">
          {num("price_in_per_m_usd", "Input $ per 1M tokens")}
          {num("price_out_per_m_usd", "Output $ per 1M tokens")}
          {num("usd_to_gbp", "$ → £ rate", "0.01")}
          {num("monthly_alert_gbp", "Alert me above £/month", "1")}
          {num("chase_cooldown_hours", "Hours between chases", "1")}
        </div>
        <CellSub style={{ margin: "14px 0 16px" }}>Introducers can have Connie chase a case once per this many hours. You can chase any time.</CellSub>
        <button className="btn btn-primary" data-testid="hub-connie-save-pricing"
                onClick={() => save({ price_in_per_m_usd: s.price_in_per_m_usd, price_out_per_m_usd: s.price_out_per_m_usd, usd_to_gbp: s.usd_to_gbp,
                  monthly_alert_gbp: s.monthly_alert_gbp, chase_cooldown_hours: s.chase_cooldown_hours }, onSaved)}>
          Save costs & limits
        </button>
      </Panel>
    </div>
  );
}

const CHANNEL_OF = { email_replies: "email", sms_replies: "sms" };

function ChannelNote({ k, ch }) {
  const c = ch?.[CHANNEL_OF[k]];
  if (!c) return null;
  const where = CHANNEL_OF[k] === "email"
    ? `Partner emails now say reply to ${c.address}`
    : `Partner texts now come from ${c.number}`;
  return (
    <div style={{ fontSize: 11.5, marginTop: 5, color: c.ready ? "var(--accent-text)" : "#B45309" }} data-testid={`hub-connie-channel-${CHANNEL_OF[k]}`}>
      {c.ready ? `${where} · a copy of every reply goes to ${c.copy_to}` : "Not set up yet: add the reply address or number to switch this on"}
    </div>
  );
}
